
import { useEffect, useRef, useState } from 'react';

export default function CoreServices() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    { icon: 'ri-robot-2-line', label: 'AI Intake Automation', desc: 'Capture and qualify every lead automatically' },
    { icon: 'ri-flow-chart', label: 'CRM Intelligence & Workflow Design', desc: 'Smart routing and pipeline management' },
    { icon: 'ri-calendar-schedule-line', label: 'Automated Scheduling & Follow-Ups', desc: 'Never miss a touchpoint again' },
    { icon: 'ri-user-voice-line', label: 'AI Assistants (Inbound + Internal)', desc: 'Always-on support for clients and teams' },
    { icon: 'ri-line-chart-line', label: 'Reporting, Forecasting & Optimization', desc: 'Data-driven decisions at every level' },
  ];

  return (
    <section id="services" ref={sectionRef} className="relative py-28 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ec1a2d1f84722bc53f18ad20912b94f2.jpg"
          alt="Technology background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#2E3440]/92"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
            Enterprise AI Infrastructure
          </h2>
          <p className="text-base text-gray-400 max-w-xl mx-auto">
            End-to-end systems designed for scale, compliance, and measurable impact.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {services.map((service, index) => (
            <div
              key={index}
              className={`group bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-[#2FB8AC]/30 transition-all duration-500 cursor-default ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${200 + index * 100}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-[#2FB8AC]/15 flex items-center justify-center mb-4 group-hover:bg-[#2FB8AC]/25 transition-colors">
                <i className={`${service.icon} text-xl text-[#2FB8AC]`}></i>
              </div>
              <h3 className="text-base font-semibold text-white mb-2">{service.label}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{service.desc}</p>
            </div>
          ))}

          {/* CTA Card */}
          <a
            href="#contact"
            className={`group flex flex-col items-center justify-center bg-[#2FB8AC]/10 backdrop-blur-sm rounded-xl p-6 border border-[#2FB8AC]/30 hover:bg-[#2FB8AC]/20 transition-all duration-500 cursor-pointer ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '700ms' }}
          >
            <div className="w-12 h-12 rounded-full bg-[#2FB8AC] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <i className="ri-arrow-right-line text-xl text-white"></i>
            </div>
            <span className="text-base font-semibold text-[#2FB8AC] whitespace-nowrap">
              Build Your AI Infrastructure
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
